import React, { useContext, useState, useEffect } from "react";
import { Offcanvas, Spinner, Tabs, Tab } from "react-bootstrap";
import SidebarContext from "./SidebarContext";
import Page from "./Page";
import ConfirmModal from "../modals/ConfirmModal";
import { longDateString } from "../../utils";

/**
 * SidebarView component - Main container for sidebar forms
 * Supports single-page and multi-page (tabbed) sidebars
 *
 * Features:
 * - Automatic tabs from Sidebar.Page children
 * - Back/Next navigation between tabs
 * - Save button on all pages
 * - Delete button only in edit mode (entity exists)
 * - Discard with confirmation modal
 *
 * Usage:
 * <SidebarView title="Add Zone">
 *   <SidebarView.Page name="basic_details" title="Basic Details">
 *     // Basic details form
 *   </SidebarView.Page>
 *   <SidebarView.Page name="devices" title="Devices">
 *     // Devices selection
 *   </SidebarView.Page>
 * </SidebarView>
 */
const SidebarView = ({
    entityName,
    children,
    placement = "end",
    width = "600px",
}) => {
    const {
        show,
        entity,
        currentPage,
        setCurrentPage,
        isSaving,
        isDeleting,
        isValid,
        onSave,
        onDelete,
        onDiscard,
        metadata,
    } = useContext(SidebarContext);

    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

    // Get all pages from children - accept any child that looks like a Page
    const pages = React.Children.toArray(children).filter(
        (child) => child && child.props && child.props.name,
    );

    // Ensure currentPage is set to first page if not specified
    const effectiveCurrentPage = currentPage || pages[0]?.props.name;

    // Find current active page
    const activePage =
        pages.find((page) => page.props.name === effectiveCurrentPage) ||
        pages[0];
    const currentPageIndex = pages.findIndex(
        (page) => page.props.name === effectiveCurrentPage,
    );
    const isFirstPage = currentPageIndex === 0 || currentPageIndex === -1;
    const isLastPage = currentPageIndex === pages.length - 1;
    const hasMultiplePages = pages.length > 1;

    // Determine if in edit mode (entity exists)
    const isEditMode = entity != null;

    // Generate title based on entity state
    const displayTitle = entity?.id
        ? entity.name || `Edit ${entityName}`
        : `Add a ${entityName}`;

    // Generate metadata from entity
    const displayMetadata = entity?.id
        ? (() => {
              const updatedBy =
                  entity.updated_by?.name ||
                  entity.created_by?.name ||
                  "Unknown";
              const updatedAt = entity.updated_at || entity.created_at;
              const dateStr = updatedAt
                  ? longDateString(new Date(updatedAt))
                  : "";

              return (
                  <div className="text-muted small mb-3">
                      <i className="fa fa-user me-1"></i>
                      {updatedBy}
                      {dateStr && (
                          <>
                              <i className="fa fa-clock ms-3 me-1"></i>
                              {dateStr}
                          </>
                      )}
                  </div>
              );
          })()
        : null;

    const handleDiscard = () => {
        setShowDiscardConfirm(true);
    };

    const handleDiscardConfirm = () => {
        setShowDiscardConfirm(false);
        if (onDiscard) {
            onDiscard();
        }
    };

    const handleDiscardCancel = () => {
        setShowDiscardConfirm(false);
    };

    const handleBack = () => {
        if (currentPageIndex > 0) {
            const prevPage = pages[currentPageIndex - 1];
            setCurrentPage(prevPage.props.name);
        }
    };

    const handleNext = () => {
        if (currentPageIndex < pages.length - 1) {
            const nextPage = pages[currentPageIndex + 1];
            setCurrentPage(nextPage.props.name);
        }
    };

    const handleSave = () => {
        if (onSave) {
            onSave();
        }
    };

    const handleDelete = () => {
        if (onDelete) {
            onDelete();
        }
    };

    const handleTabSelect = (tabKey) => {
        if (tabKey) {
            setCurrentPage(tabKey);
        }
    };

    const handleClose = () => {
        handleDiscard();
    };

    return (
        <Offcanvas
            show={show}
            onHide={handleClose}
            placement={placement}
            style={{ width }}
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>{displayTitle}</Offcanvas.Title>
            </Offcanvas.Header>

            {displayMetadata && (
                <div className="px-3 py-2 bg-light border-bottom">
                    {displayMetadata}
                </div>
            )}

            <Offcanvas.Body>
                <Tabs
                    id="sidebar-tabs"
                    activeKey={effectiveCurrentPage}
                    onSelect={handleTabSelect}
                    className="mb-3"
                >
                    {pages.map((page) => (
                        <Tab
                            key={page.props.name}
                            eventKey={page.props.name}
                            title={page.props.title || page.props.name}
                        >
                            {page}
                        </Tab>
                    ))}
                </Tabs>
            </Offcanvas.Body>

            <div className="offcanvas-footer border-top p-3 d-flex justify-content-between">
                <div>
                    {isEditMode && (
                        <button
                            className="btn btn-danger"
                            onClick={handleDelete}
                            disabled={isSaving || isDeleting}
                        >
                            {isDeleting ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Deleting...
                                </>
                            ) : (
                                "Delete"
                            )}
                        </button>
                    )}
                </div>

                <div className="d-flex gap-2">
                    <button
                        className="btn btn-secondary"
                        onClick={handleDiscard}
                        disabled={isSaving || isDeleting}
                    >
                        Discard
                    </button>

                    {hasMultiplePages && !isFirstPage && (
                        <button
                            className="btn btn-outline-primary"
                            onClick={handleBack}
                            disabled={isSaving || isDeleting}
                        >
                            Back
                        </button>
                    )}

                    {hasMultiplePages && !isLastPage && (
                        <button
                            className="btn btn-primary"
                            onClick={handleNext}
                            disabled={isSaving || isDeleting}
                        >
                            Next
                        </button>
                    )}

                    <button
                        className="btn btn-success"
                        onClick={handleSave}
                        disabled={!isValid || isSaving || isDeleting}
                        title={
                            !isValid
                                ? "Please complete all required fields"
                                : ""
                        }
                    >
                        {isSaving ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Saving...
                            </>
                        ) : (
                            "Save"
                        )}
                    </button>
                </div>
            </div>

            <ConfirmModal
                open={showDiscardConfirm}
                type="warning"
                title="Discard Changes"
                content="Are you sure you want to discard your changes? This action cannot be undone."
                onConfirm={handleDiscardConfirm}
                onCancel={handleDiscardCancel}
                onClose={handleDiscardCancel}
            />
        </Offcanvas>
    );
};

// Attach Page as a static property for compound component pattern
SidebarView.Page = Page;

export default SidebarView;
